/* host-overlays.js — The trust corner steps back while the host page has a dialog or drawer open.
 *
 * A page's own overlay can never stand above the loader: the loader's surfaces sit at the top of the
 * z-index range, so whatever z-index a drawer picks, a trust card lands on top of it, in the very corner
 * a drawer slides in from. The page cannot fix that and the loader must not move into the page, so the
 * trust corner yields instead: while an overlay is on screen it fades out.
 *
 * The trust corner only. The bubbles (with a campaign card or the unread card in their slot) and the
 * launcher stay (Armin's decision, 2026-10-06): they are the way into the chat, and a
 * visitor reading a drawer is the visitor they are there for. The proof is what can wait.
 *
 * Paused, not retired. Nothing is dismissed, rescheduled or marked as seen: closing the overlay brings
 * back exactly the card that stood there.
 *
 * What counts as an overlay is what pages already say about themselves: an open <dialog>, an element
 * with aria-modal="true", or one carrying data-leadwin-overlay, for a drawer that is not modal (the page
 * beside it stays usable) and so must not claim aria-modal. Only one that is actually on screen counts:
 * many drawers stay in the document while closed, pushed off the edge by a transform.
 *
 * The host page is only read. What changes is a style sheet of the loader's own, which also covers a
 * surface built while the overlay is open.
 */

const overlaySelector = 'dialog[open],[aria-modal="true"],[data-leadwin-overlay]';

/** The loader's own surfaces: its campaign modal is an aria-modal dialog too, and must not pause the corner. */
const ownSurfaceSelector = '.lw-nudges,.lw-trust-corner,.lw-announcement-backdrop';

const styleId = 'lw-host-overlay-styles';

/** The resting rule is what fades the paused corner back in; without it the way back would be a jump. */
const restingCss =
    '.lw-trust-corner{transition:opacity .2s ease}' +
    '@media (prefers-reduced-motion:reduce){.lw-trust-corner{transition:none}}';

/* !important because it competes with leadwin-nudge.css's own visibility switch at equal specificity, in
 * whatever order the two ended up in <head>. Visibility waits for the fade, and takes the corner out of
 * the tab order and out of reach of a click aimed at the overlay below them. */
const pausedCss =
    '.lw-trust-corner{opacity:0!important;visibility:hidden!important;' +
    'transition:opacity .2s ease,visibility 0s linear .2s!important}' +
    '@media (prefers-reduced-motion:reduce){.lw-trust-corner{transition:none!important}}';

/**
 * Whether an element is laid out somewhere inside the viewport. A closed drawer that is only pushed off
 * the edge passes every visibility check and fails this one.
 * @param {Element} element
 * @returns {boolean}
 */
function isOnScreen(element) {
    if (typeof element.checkVisibility === 'function' && !element.checkVisibility({ visibilityProperty: true })) {
        return false;
    }

    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0
        && rect.right > 0 && rect.bottom > 0
        && rect.left < window.innerWidth && rect.top < window.innerHeight;
}

/**
 * Whether the host page has an overlay of its own on screen right now.
 * @returns {boolean}
 */
export function hostOverlayOpen() {
    const candidates = document.querySelectorAll(overlaySelector);

    for (let i = 0; i < candidates.length; i++) {
        if (!candidates[i].closest(ownSurfaceSelector) && isOnScreen(candidates[i])) {
            return true;
        }
    }

    return false;
}

/**
 * Writes the rule the page calls for. Compares with what stands instead of remembering a state, so a
 * second loader on the same page, or a head that was emptied, can never leave a stale rule behind.
 */
function applyOverlayState() {
    const css = hostOverlayOpen() ? pausedCss : restingCss;
    let style = document.getElementById(styleId);

    if (!style) {
        style = document.createElement('style');
        style.id = styleId;
        document.head.appendChild(style);
    }

    if (style.textContent !== css) {
        style.textContent = css;
    }
}

/**
 * Starts watching the host page for overlays. Once per loader, after the page has loaded.
 *
 * Every mutation batch only schedules a check, and one check runs per task however many batches came:
 * the check reads layout, and a busy application changes classes all the time. The transitionend hook is
 * for the drawer that slides in: when its class flips it is still off screen, and only the end of the
 * slide says it has arrived.
 */
export function watchHostOverlays() {
    applyOverlayState();

    if (typeof MutationObserver !== 'function') {
        return; // The corner simply stays, as it did before this existed.
    }

    let pending = null;

    function scheduleCheck() {
        if (pending === null) {
            pending = setTimeout(function () {
                pending = null;
                applyOverlayState();
            }, 0);
        }
    }

    new MutationObserver(scheduleCheck).observe(document.documentElement, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['open', 'aria-modal', 'data-leadwin-overlay', 'hidden', 'class', 'style']
    });
    document.addEventListener('transitionend', scheduleCheck, true);
}
